import { useTranslation } from 'react-i18next';

import type { SocialBulkResult, SocialPost } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { displayPostTitle } from '../schemas/post-state';

export interface BulkOutcomeAlertProps {
  result: SocialBulkResult;
  /** The rows the action ran on, for titles in the failure list. */
  rows: readonly SocialPost[];
  onDismiss: () => void;
}

/** "3 done, 1 failed" with each failure's title and the single-route error message. */
export function BulkOutcomeAlert({ result, rows, onDismiss }: BulkOutcomeAlertProps) {
  const { t } = useTranslation(['social', 'common']);
  const failed = result.results.filter((r) => !r.ok);
  const ok = result.results.length - failed.length;
  const titleOf = (id: string) => {
    const row = rows.find((r) => r.id === id);
    return row ? displayPostTitle(row) : id;
  };
  return (
    <Alert variant={failed.length > 0 ? 'destructive' : 'default'} role="status" className="mb-3">
      <AlertTitle>{t('social:bulk.result', { ok, failed: failed.length })}</AlertTitle>
      <AlertDescription>
        {failed.length > 0 ? (
          <ul className="list-disc pl-4">
            {failed.map((outcome) => (
              <li key={outcome.id}>
                {titleOf(outcome.id)}
                {': '}
                {outcome.error
                  ? t(`errors:code.${outcome.error.code}`, { defaultValue: outcome.error.message })
                  : t('social:bulk.unknownError')}
              </li>
            ))}
          </ul>
        ) : null}
        <Button type="button" variant="ghost" size="xs" onClick={onDismiss} className="-ml-2">
          {t('common:actions.close')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
